'use client';

import { CHAT_UNAVAILABLE_MESSAGE } from '@shared/all/domains/messages';
import { buildChatLink, CHAT_ROUTE } from '@shared/all/helpers/chatLink';
import { useConversations } from '@shared/utils/hooks/useConversations';
import useUser from '@shared/utils/hooks/useUser';
import { MessageCircle } from 'lucide-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { client } from '../../../utils/trpc';
import { useChatWorkspace } from './ChatWorkspaceContext';

type Props = {
  // /chat/<conversationId> — a link to one chat by id.
  conversationId?: string;
  // /chat/<conversationId>/m/<messageId> — that chat, opened at one message.
  messageId?: number;
  // /chat/s/<spaceSlug>[/f/<factiiiId>] — a link to the chat a space or a
  // factiii owns. That chat may have no id yet: it gets one from the first
  // message, so addressing it by owner is the only link that works before
  // anyone writes in it.
  spaceSlug?: string;
  factiiiId?: number;
};

/**
 * /chat is the chat workspace opened full screen. The workspace itself is
 * mounted in Main, so this route only sets its mode and resolves what the URL
 * points at — nothing renders here unless that fails.
 */
const ChatPage = (props: Props) => {
  const { currentUser } = useUser();
  const { conversationsState, addConversations } = useConversations();
  const workspace = useChatWorkspace();
  const searchParams = useSearchParams();
  // ?s=<spaceSlug>&f=<factiiiId> is the older form of the same link, still
  // live in bookmarks and in older builds of the mobile app.
  const spaceSlug = props.spaceSlug ?? searchParams?.get('s') ?? null;
  const factiiiParam = props.factiiiId ?? searchParams?.get('f') ?? null;
  const factiiiId = factiiiParam ? Number(factiiiParam) : null;
  const isAuthenticated = Boolean(currentUser.isAuthenticated);
  // Set when the link points at a chat this account cannot open.
  const [denied, setDenied] = useState<string | null>(null);

  const {
    enterFullscreen,
    dismissFullscreen,
    selectSpace,
    openChat,
    openVirtual
  } = workspace;
  const knownConversation = conversationsState.conversations.some(
    (c) => c.id === props.conversationId
  );

  // One effect: landing here is the event. It puts the workspace in full
  // screen, then resolves whatever the URL asks for.
  useEffect(() => {
    if (!isAuthenticated) return;
    enterFullscreen();
    let ignore = false;

    if (props.conversationId) {
      const focus = props.messageId
        ? { messageId: props.messageId }
        : undefined;
      // Already in context (opened from the sidebar, or a live socket
      // conversation) — no round trip needed.
      if (knownConversation) {
        openChat(props.conversationId, focus);
        return;
      }
      void resolveConversation(props.conversationId).then((result) => {
        if (ignore) return;
        if (!result.conversation) {
          // Stay on the link so the reader sees why it did not open.
          setDenied(result.error);
          dismissFullscreen();
          return;
        }
        addConversations([result.conversation]);
        openChat(result.conversation.id, focus);
      });
      return () => {
        ignore = true;
      };
    }

    if (!spaceSlug) return;
    selectSpace(spaceSlug);
    // Without `create` this is a pure lookup+join — a deep link to a chat
    // nobody has messaged in yet opens the virtual thread instead of writing
    // a row to the DB.
    void client.messages.openDefaultChannel
      .mutate({
        spaceSlug,
        factiiiId: factiiiId ?? undefined
      })
      .then(([error, result]) => {
        if (ignore) return;
        if (error || !result) {
          // Same treatment as a chat id nobody may open: say so on the link
          // rather than dumping the reader somewhere else.
          setDenied(error?.message ?? DENIED_MESSAGE);
          dismissFullscreen();
          return;
        }
        if (result.channel) {
          addConversations([result.channel]);
          openChat(result.channel.id);
        } else {
          openVirtual({
            spaceSlug,
            factiiiId,
            name: result.name
          });
        }
      });
    return () => {
      ignore = true;
    };
  }, [
    props.conversationId,
    props.messageId,
    spaceSlug,
    factiiiId,
    isAuthenticated
  ]);

  // Signing in comes back to the same link — the chat, and the message in it.
  const linkPath = props.conversationId
    ? buildChatLink({
        conversationId: props.conversationId,
        messageId: props.messageId
      })
    : spaceSlug
      ? buildChatLink({ spaceSlug, factiiiId })
      : CHAT_ROUTE;

  return (
    <AppLayout fullWidth>
      <div className="flex size-full flex-col items-center justify-center gap-3 p-6 text-center">
        <MessageCircle className="size-10 text-textSecondary/50" />
        {!isAuthenticated ? (
          <>
            <p className="text-textSecondary">Sign in to use chat.</p>
            <Link
              href={`/login?redirect=${encodeURIComponent(linkPath)}`}
              className="rounded-lg bg-primary px-4 py-2 font-emphasis text-white transition-all hover:bg-primary/90"
            >
              Sign in
            </Link>
          </>
        ) : denied ? (
          <>
            <p className="max-w-sm text-textSecondary">{denied}</p>
            <Link
              href={CHAT_ROUTE}
              className="rounded-lg bg-primary px-4 py-2 font-emphasis text-white transition-all hover:bg-primary/90"
            >
              Go to your chats
            </Link>
          </>
        ) : (
          <p className="text-textSecondary">Loading chat…</p>
        )}
      </div>
    </AppLayout>
  );
};

const DENIED_MESSAGE = CHAT_UNAVAILABLE_MESSAGE;

/**
 * A link may point at a chat the viewer is already in, at a public channel
 * they may join, or at one that is none of their business. getById covers the
 * first, joinChannel the second, and anything else comes back as the message
 * the page shows instead of the chat.
 */
const resolveConversation = async (conversationId: string) => {
  const [, conversation] = await client.messages.getById.query({
    conversationId
  });
  if (conversation) return { conversation, error: null };

  const [, channel] = await client.messages.joinChannel.mutate({
    conversationId
  });
  if (channel) return { conversation: channel, error: null };

  // Both failures say the same thing to the reader on purpose: whether the
  // chat exists is not this account's business.
  return { conversation: null, error: DENIED_MESSAGE };
};

export default ChatPage;
